import { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  FileText, 
  MessageSquare, 
  LogOut, 
  Plus,
  Menu,
  X
} from 'lucide-react';
import adminApi from '@/lib/adminApi';

// Admin Pages
import AdminBlogList from './AdminBlogList';
import AdminBlogEditor from './AdminBlogEditor';
import AdminContacts from './AdminContacts';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const checkAuth = useCallback(async () => {
    try {
      const response = await adminApi.get('/api/admin/me');
      setUser(response.data);
    } catch (error) {
      navigate('/admin/login');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const handleLogout = async () => {
    try {
      await adminApi.post('/api/admin/logout');
    } finally {
      navigate('/admin/login');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  const navItems = [
    { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/admin/blog', icon: FileText, label: 'Blog Posts' },
    { path: '/admin/contacts', icon: MessageSquare, label: 'Contacts' },
  ];

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <div className="min-h-screen bg-[#050505] flex" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside 
        className={`admin-sidebar fixed inset-y-0 left-0 z-50 w-64 transform transition-transform lg:translate-x-0 lg:static ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="p-6 border-b border-[#27272A]">
            <Link to="/" className="text-xl font-bold tracking-tight">
              <span className="text-white">TERRY</span>
              <span className="text-[#007AFF]">WEBSTER</span>
            </Link>
            <p className="text-[#52525B] text-sm mt-1">Admin Panel</p>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`admin-nav-item ${isActive(item.path) ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
                data-testid={`admin-nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <item.icon size={20} />
                {item.label}
              </Link>
            ))}
          </nav>

          {/* User & Logout */}
          <div className="p-4 border-t border-[#27272A]">
            <p className="text-[#A1A1AA] text-sm mb-3 truncate">{user?.email}</p>
            <button
              onClick={handleLogout}
              className="admin-nav-item w-full text-red-400 hover:bg-red-500/10"
              data-testid="admin-logout-button"
            >
              <LogOut size={20} />
              Logout
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top Bar */}
        <header className="sticky top-0 z-30 bg-[#0C0C0C] border-b border-[#27272A] px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="lg:hidden p-2 text-white"
            aria-label="Toggle sidebar"
          >
            {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          <div className="flex items-center gap-4 ml-auto">
            <Link
              to="/admin/blog/new"
              className="btn-primary flex items-center gap-2 text-sm"
              data-testid="new-post-button"
            >
              <Plus size={18} />
              New Post
            </Link>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-6">
          <Routes>
            <Route path="/dashboard" element={<DashboardHome />} />
            <Route path="/blog" element={<AdminBlogList />} />
            <Route path="/blog/new" element={<AdminBlogEditor />} />
            <Route path="/blog/edit/:id" element={<AdminBlogEditor />} />
            <Route path="/contacts" element={<AdminContacts />} />
            <Route path="*" element={<DashboardHome />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

// Dashboard Home Component
function DashboardHome() {
  const [stats, setStats] = useState({ posts: 0, contacts: 0 });
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const [postsRes, contactsRes] = await Promise.all([
        adminApi.get('/api/admin/blog'),
        adminApi.get('/api/admin/contacts')
      ]);
      setStats({
        posts: postsRes.data.length,
        contacts: contactsRes.data.length
      });
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
      /* stats stay at 0 when the fetch fails */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return (
    <div data-testid="dashboard-home">
      <h1 className="text-3xl font-bold mb-8">Dashboard</h1>
      
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="spinner" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="p-6 border border-[#27272A] bg-[#0C0C0C]">
            <FileText className="w-8 h-8 text-[#007AFF] mb-4" />
            <p className="text-4xl font-bold">{stats.posts}</p>
            <p className="text-[#A1A1AA]">Blog Posts</p>
          </div>
          <div className="p-6 border border-[#27272A] bg-[#0C0C0C]">
            <MessageSquare className="w-8 h-8 text-[#007AFF] mb-4" />
            <p className="text-4xl font-bold">{stats.contacts}</p>
            <p className="text-[#A1A1AA]">Contact Messages</p>
          </div>
        </div>
      )}

      <div className="mt-12">
        <h2 className="text-xl font-bold mb-4">Quick Actions</h2>
        <div className="flex flex-wrap gap-4">
          <Link to="/admin/blog/new" className="btn-primary flex items-center gap-2">
            <Plus size={18} />
            Create New Post
          </Link>
          <Link to="/admin/contacts" className="btn-secondary flex items-center gap-2">
            <MessageSquare size={18} />
            View Messages
          </Link>
          <Link to="/" className="btn-secondary" target="_blank">
            View Website →
          </Link>
        </div>
      </div>
    </div>
  );
}
